import React, { useContext, useEffect, useRef } from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHome, faFilm, faTv, faUser, faBookmark, 
  faRandom, faUsers, faCalendarAlt, faNewspaper, 
  faClock
} from '@fortawesome/free-solid-svg-icons';
import { faDiscord } from '@fortawesome/free-brands-svg-icons';
import { AppContext } from '../../context/AppContext';
import { AuthContext } from '../../context/AuthContext';

const MenuDropdown = styled.div`
  position: fixed;
  top: 80px;
  left: 20px;
  width: 220px;
  background-color: rgba(26, 26, 26, 0.75);
  backdrop-filter: blur(12px);
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  z-index: 200;
  overflow: hidden;
  transition: all 0.3s ease;
  opacity: ${props => props.$isOpen ? 1 : 0};
  transform: ${props => props.$isOpen ? 'translateY(0) scale(1)' : 'translateY(-10px) scale(0.98)'};
  pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  border: 1px solid rgba(255, 255, 255, 0.12);
  
  @media (max-width: 768px) {
    width: 180px;
    left: 15px;
  }
  
  @media (max-width: 480px) {
    width: 160px;
    left: 15px;
  }
`;

const MenuSection = styled.div`
  padding: 10px;
`;

const SectionTitle = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  color: var(--text-dim);
  padding: 7px 10px;
  margin-top: 4px;
  letter-spacing: 1.2px;
  font-weight: 600;
  opacity: 0.8;
`;

const MenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MenuItem = styled(NavLink)`
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: var(--text-light);
  font-size: 13px;
  font-weight: 500;
  border-radius: 7px;
  transition: all 0.2s ease;
  margin: 2px 0;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.25);
    transform: translateX(3px);
  }
  
  &.active {
    background-color: rgba(59, 130, 246, 0.3);
    color: white;
    font-weight: 600;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.2);
  }
  
  svg {
    margin-right: 10px;
    width: 14px;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
  }
  
  &.active svg {
    color: var(--primary-light);
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
    
    svg {
      margin-right: 8px;
    }
  }
`;

const ExternalMenuItem = styled.a`
  display: flex;
  align-items: center;
  padding: 10px 15px;
  color: var(--text-light);
  font-size: 13px;
  font-weight: 500;
  border-radius: 7px;
  transition: all 0.2s ease;
  margin: 2px 0;
  
  &:hover {
    background-color: rgba(114, 137, 218, 0.25);
    transform: translateX(3px);
  }
  
  svg {
    margin-right: 10px;
    width: 14px;
    text-align: center;
    color: rgba(255, 255, 255, 0.7);
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
    font-size: 12px;
    
    svg {
      margin-right: 8px;
    }
  }
`;

const Sidebar = () => {
  const { sidebarOpen, closeSidebar } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);
  const menuRef = useRef(null);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarOpen && menuRef.current && !menuRef.current.contains(event.target)) {
        //check if the click was on the menu toggle button
        const menuToggleButton = document.querySelector('.menu-toggle-button');
        if (!menuToggleButton || !menuToggleButton.contains(event.target)) {
          closeSidebar();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [sidebarOpen, closeSidebar]);
  
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && sidebarOpen) {
        closeSidebar();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [sidebarOpen, closeSidebar]);
  
  return (
    <MenuDropdown $isOpen={sidebarOpen} ref={menuRef}>
      <MenuSection>
        <MenuList>
          <li>
            <MenuItem to="/" end onClick={closeSidebar}>
              <FontAwesomeIcon icon={faHome} />
              <span>Home</span>
            </MenuItem>
          </li>
          <li>
            <MenuItem to="/movies" onClick={closeSidebar}>
              <FontAwesomeIcon icon={faFilm} />
              <span>Movies</span>
            </MenuItem>
          </li>
          <li>
            <MenuItem to="/shows" onClick={closeSidebar}>
              <FontAwesomeIcon icon={faTv} />
              <span>TV Shows</span>
            </MenuItem>
          </li>
        </MenuList>
      </MenuSection>
      
      {currentUser && (
        <MenuSection>
          <SectionTitle>Personal</SectionTitle>
          <MenuList>
            <li>
              <MenuItem to="/profile" onClick={closeSidebar}>
                <FontAwesomeIcon icon={faUser} />
                <span>Profile</span>
              </MenuItem>
            </li>
            <li>
              <MenuItem to="/profile" onClick={closeSidebar}>
                <FontAwesomeIcon icon={faBookmark} />
                <span>Watchlist</span>
              </MenuItem>
            </li>
          </MenuList>
        </MenuSection>
      )}
      
      <MenuSection>
        <SectionTitle>Community</SectionTitle>
        <MenuList>
          <li>
            <ExternalMenuItem 
              href="https://discord.gg/5fKH3TYVmE" 
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeSidebar}
            >
              <FontAwesomeIcon icon={faDiscord} />
              <span>Discord</span>
            </ExternalMenuItem>
          </li>
        </MenuList>
      </MenuSection>
    </MenuDropdown>
  );
};

export default Sidebar;